'use client'

import React, { useRef, useState } from 'react'
import Image from '@/components/Image'
import Link from 'next/link'
import { ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react'
import { cn } from '@/utilities/cn'
import { createClientLogger } from '@/lib/logger/client'

const log = createClientLogger('PartnersShowcase')

export interface Partner {
  id: string
  name: string
  description?: string
  logo: {
    url: string
    alt?: string
    width?: number
    height?: number
  }
  website?: string // External website URL
  page?: string // Internal page URL
  featured?: boolean
  active?: boolean
  order?: number
}

export interface PartnersShowcaseProps {
  partners: Partner[]
  title?: string
  subtitle?: string
  showTitle?: boolean
  className?: string
  variant?: 'default' | 'minimal' | 'cards'
  itemsPerView?: number
}

export const PartnersShowcase: React.FC<PartnersShowcaseProps> = ({
  partners,
  title = 'Onze Partners',
  subtitle,
  showTitle = true,
  className,
  variant = 'default',
  itemsPerView = 4,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const scroll = (direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current
      const scrollAmount = container.clientWidth / 2
      const newScrollLeft = direction === 'left'
        ? container.scrollLeft - scrollAmount
        : container.scrollLeft + scrollAmount

      container.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      })
    }
  }

  // Filter out inactive partners and sort: featured first, then by order, then by name
  const sortedPartners = [...partners]
    .filter(partner => partner.active !== false) // Only show active partners
    .sort((a, b) => {
      if (a.featured && !b.featured) return -1
      if (!a.featured && b.featured) return 1
      if (a.order !== b.order) return (a.order || 0) - (b.order || 0)
      return a.name.localeCompare(b.name)
    })

  if (sortedPartners.length === 0) {
    return null
  }

  const needsScrolling = sortedPartners.length > itemsPerView

  const PartnerItem = ({ partner, index }: { partner: Partner; index: number }) => {
    const [imageError, setImageError] = useState(false)
    
    // Transform the image URL to use current domain if needed
    const getImageUrl = (url: string) => {
      // If the URL points to jeugdfilms.local but we're on a different domain, 
      // transform it to use the current domain
      if (url.includes('jeugdfilms.local') && typeof window !== 'undefined') {
        const currentHost = window.location.host
        if (currentHost !== 'jeugdfilms.local') {
          return url.replace('jeugdfilms.local', currentHost).replace('https://', window.location.protocol + '//')
        }
      }
      return url
    }

    const partnerContent = (
      <div
        key={partner.id}
        className={cn(
          'flex-shrink-0 flex items-center justify-center p-6 transition-all duration-300',
          variant === 'cards' && 'bg-white rounded-lg shadow-sm border border-slate-200 hover:shadow-md',
          variant === 'minimal' && 'hover:opacity-80',
          (partner.website || partner.page) && 'cursor-pointer'
        )}
        style={{ width: `${100 / itemsPerView}%`, minWidth: '200px' }}
      >
        <div className="relative w-full h-20 flex items-center justify-center">
          {imageError || partner.logo.url.includes('favicon.svg') ? (
            // Fallback: Use regular img tag if Next.js Image fails
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={getImageUrl(partner.logo.url)}
              alt={partner.logo.alt || partner.name}
              className="max-w-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
              style={{
                width: 'auto',
                height: partner.logo.url.endsWith('.svg') ? '60px' : 'auto',
                maxWidth: '160px',
                maxHeight: '80px',
                minHeight: '40px',
              }}
              onError={() => {
                log.error({ url: partner.logo.url, name: partner.name }, 'Image failed to load (fallback)')
              }}
            />
          ) : (
            <Image
              src={getImageUrl(partner.logo.url)}
              alt={partner.logo.alt || partner.name}
              width={partner.logo.url.endsWith('.svg') ? 128 : (partner.logo.width || 160)}
              height={partner.logo.url.endsWith('.svg') ? 128 : (partner.logo.height || 80)}
              className="max-w-full object-contain filter grayscale hover:grayscale-0 transition-all duration-300"
              style={{
                width: 'auto',
                height: partner.logo.url.endsWith('.svg') ? '60px' : 'auto',
                maxWidth: '160px',
                maxHeight: '80px',
                minHeight: '40px',
              }}
              onError={(e) => {
                log.warn({ url: partner.logo.url, name: partner.name }, 'Next.js Image failed, using fallback')
                setImageError(true)
              }}
              unoptimized={true}
              priority={false}
            />
          )}
        </div>
        {variant === 'cards' && partner.description && (
          <div className="mt-4 text-center">
            <h4 className="font-medium text-slate-900 mb-1">{partner.name}</h4>
            <p className="text-sm text-slate-600">{partner.description}</p>
          </div>
        )}
      </div>
    )

    // Determine primary link: prefer page (internal) over website (external)
    const primaryLink = partner.page || partner.website
    const isExternal = !partner.page && partner.website

    if (primaryLink) {
      return (
        <Link
          key={partner.id}
          href={primaryLink}
          target={isExternal ? "_blank" : undefined}
          rel={isExternal ? "noopener noreferrer" : undefined}
          className="no-underline"
        >
          {partnerContent}
        </Link>
      )
    }

    return partnerContent
  }

  return (
    // Full-bleed escape — same trick as HeroSection; needs overflow-x:hidden on body (globals.scss).
    <div className={cn('py-12 bg-slate-50 relative left-1/2 -mx-[50vw] w-screen', className)}>
      <div className="container">
        {showTitle && (
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-slate-900 mb-4">
              {title}
            </h2>
            {subtitle && (
              <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                {subtitle}
              </p>
            )}
          </div>
        )}

        <div className="relative">
          {needsScrolling && (
            <>
              <button
                onClick={() => scroll('left')}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:shadow-xl transition-shadow"
                aria-label="Scroll left"
              >
                <ChevronLeft className="w-5 h-5 text-slate-600" />
              </button>
              <button
                onClick={() => scroll('right')}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-2 hover:shadow-xl transition-shadow"
                aria-label="Scroll right"
              >
                <ChevronRight className="w-5 h-5 text-slate-600" />
              </button>
            </>
          )}

          <div
            ref={scrollContainerRef}
            className={cn(
              'flex overflow-x-auto scrollbar-hide gap-6',
              needsScrolling ? 'px-12' : 'justify-center'
            )}
            style={{
              scrollSnapType: 'x mandatory',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
            }}
          >
            {sortedPartners.map((partner, index) => (
              <div
                key={partner.id}
                style={{ scrollSnapAlign: 'start' }}
                className="flex-shrink-0"
              >
                <PartnerItem partner={partner} index={index} />
              </div>
            ))}
          </div>
        </div>

        {sortedPartners.length > 6 && (
          <div className="text-center mt-8">
            <p className="text-sm text-slate-500">
              Scroll horizontaal om alle {sortedPartners.length} partners te bekijken
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
